import cx from "classnames";
import { useRef, useEffect, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import RenamePopover from "@/appComponents/Blocks/Composites/RenamePopover/RenamePopover";
import { ContextMenu } from "@/appComponents/Blocks/Composites/ContextMenu/ContextMenu";
import {
  Divider,
  Flex,
  Tabs,
  Text,
  TogglePill,
  TogglePillGroup,
  ToolIcon
} from "@theorgchartdev/storybook";
import { DoBoxLayoutEditor } from "../../ContextualToolbar/ToolbarOptions";
import { getHoverTipContent } from "@/Utils/Misc";
import { GetWSMgr } from "@/Utils/StateLib";
import { LoadBoxStyles, SaveBoxStyles } from "@/Utils/Promises";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { enumChartElement, enumBoxStyleSet, enumHierarchyTypes } from "@coreapp/constants";
import { QBoxStylesPanel } from "@coreapp/CoreApp/QBoxStylesPanel";
import { EventToCanvas } from "@coreapp/utils/EventToCanvas";
import { TruncateText } from "@coreapp/utils/TruncateText";
import { appstr } from "@coreapp/utils/appstr";
import { resizeCanvas } from "@coreapp/utils/resizeCanvas";
import { setupCanvas } from "@coreapp/utils/setupCanvas";
import { useTopic } from "@/hooks/useTopic";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";

const GetToolOptions = (bsPanel, handleToolClick) => {
  const wsMgr = StateLib.GetWSMgr();
  const bsMgr = wsMgr.GetBoxStylesMgr();
  var numSel = bsPanel.GetNumSelectedElements();
  var oneLayoutSelected = numSel == 1;
  var enableShuffleUp = false;
  var enableShuffleDown = false;
  if (oneLayoutSelected) {
    var selectedBox = bsPanel.GetSelectedBoxStyle();
    if (selectedBox) {
      var l = selectedBox.GetLayout();
      enableShuffleUp = !bsMgr.IsFirstLayout(l);
      enableShuffleDown = !bsMgr.IsLastLayout(l);
    }
  }
  let theWSMgr = StateLib.GetWSMgr();
  var selection = theWSMgr.GetFirstSelectedElement(enumChartElement.chartBox);
  var selCount = theWSMgr.GetNumSelectedElements();
  var oneBoxSelected = selCount == 1 && selection != null;

  var theDoc = StateLib.GetDoc();
  var enableOpt = theDoc != null;
  var enableAdd = enableOpt && theDoc.GetRootObj() !== null;
  var singleSel = oneBoxSelected && oneLayoutSelected;

  let doAdd = true;
  let doReplace = true;
  let doReorder = true;
  let doSave = true;
  let doReset = true;
  let bsDirty = theWSMgr.BoxStylesDirty(bsPanel.fCurBoxStyleSet);

  return (
    <ToolbarContainer>
      {doAdd && (
        <ToolIcon
          size={"medium-small"}
          name={"plus"}
          modifier={"secondary"}
          disabled={!enableAdd}
          key={0}
          dataTestId={"boxlibrary--add"}
          onClick={() => handleToolClick("AddBoxStyle")}
          tooltipProps={getHoverTipContent("addbs")}
        />
      )}
      <div className={"ocn-toolbar-horizontal-divider"}>
        <Divider vertical />
      </div>
      {doReplace && (
        <ToolIcon
          key={1}
          name={"arrow-right-arrow-left"}
          size="medium-small"
          disabled={!singleSel}
          dataTestId={"boxlibrary--replace"}
          onClick={() => handleToolClick("ReplaceBoxStyle")}
          tooltipProps={getHoverTipContent("replacebs")}
        />
      )}
      <div className={"ocn-toolbar-horizontal-divider"}>
        <Divider vertical />
      </div>
      {doReorder && (
        <ToolIcon
          key={2}
          name={"caret-up"}
          title={"Shuffle up"}
          size="medium-small"
          disabled={!enableShuffleUp}
          dataTestId={"boxlibrary--shuffle-up"}
          onClick={() => handleToolClick("ShuffleBoxStyleUp")}
          tooltipProps={getHoverTipContent("reorderbsup")}
        />
      )}
      {doReorder && (
        <ToolIcon
          key={3}
          name={"caret-down"}
          title={"Shuffle down"}
          size="medium-small"
          disabled={!enableShuffleDown}
          dataTestId={"boxlibrary--shuffle-down"}
          onClick={() => handleToolClick("ShuffleBoxStyleDown")}
          tooltipProps={getHoverTipContent("reorderbsdown")}
        />
      )}
      <Flex className={"oc-right-justify"} gap={"12px"}>
        {doSave && bsDirty && (
          <ToolIcon
            key={4}
            name={"check"}
            title={"Save"}
            size="medium-small"
            dataTestId={"boxlibrary--save"}
            onClick={() => handleToolClick("SaveBoxStyles")}
            tooltipProps={getHoverTipContent("savebs")}
          />
        )}
        {doReset && bsDirty && (
          <ToolIcon
            key={5}
            name={"xmark"}
            title={"Reset"}
            size="medium-small"
            dataTestId={"boxlibrary--reset"}
            onClick={() => handleToolClick("LoadBoxStyles")}
            tooltipProps={getHoverTipContent("reloadbs")}
          />
        )}
      </Flex>
    </ToolbarContainer>
  );
};

const GetSystemTools = (bsPanel, handleToolClick) => {
  const numSel = bsPanel.GetNumSelectedElements();
  const oneLayoutSelected = numSel == 1;

  return (
    <ToolbarContainer>
      <ToolIcon
        size={"medium-small"}
        name={"file-export"}
        modifier={"secondary"}
        disabled={!oneLayoutSelected}
        dataTestId={"boxlibrary--move-to-shared"}
        onClick={() => handleToolClick("CopyBoxStyle")}
        tooltipProps={getHoverTipContent("copyboxstyle")}
      />
    </ToolbarContainer>
  );
};

const ToolbarContainer = props => (
  <Flex height={"28px"} overflow={"hidden"} alignItems={"center"} gap={"8px"} padding={"4px 4px"}>
    {props.children}
  </Flex>
);

const GetTabs = (panel, showControls, handleToolClick) => {
  //removed User from Box style tabs from UI for time being - OCN5-2823
  const bsSets = ["System", "Shared"];
  const wsMgr = GetWSMgr();
  let options = [];
  for (let aSet in bsSets) {
    let theSet = bsSets[aSet];
    let badgeProps = {
      showBadge: aSet && wsMgr.BoxStylesDirty(aSet),
      border: false,
      color: "warning",
      size: "small",
      maxLength: 12,
      badgeContent: appstr("Unsaved")
    };
    let option = {
      header: appstr(theSet),
      badgeProps: badgeProps,
      dataTestId: `boxlibrary--set-${theSet}`
    };
    if (showControls) {
      if (theSet === "System") {
        option.content = GetSystemTools(panel, handleToolClick);
      } else {
        option.content = GetToolOptions(panel, handleToolClick);
      }
    }
    options.push(option);
  }
  return options;
};

const GetPanelDimensions = customWidth => {
  let width = customWidth || 300;
  let height = 1500;

  var sidebar = document.querySelector(".sideContainer");
  if (sidebar) {
    const rect = sidebar.getBoundingClientRect();
    height = rect.height;
  }
  return { width, height };
};

const BoxLibrary = ({
  onBoxSelect,
  showTabs, // conditionally render tabs
  hierarchyType, // conditionally render hierarchy type
  currentBoxStyleSet, // conditionally render single set
  showControls, // conditionally render edit controls
  scaleFactor = 1.0,
  customHeight,
  customWidth,
  minWidth = 300
}) => {
  const wsMgr = StateLib.GetWSMgr();
  const doc = wsMgr.GetDoc();
  const isPositionChart = doc?.IsPositionDocument() ?? false;
  const bsMgr = wsMgr.GetBoxStylesMgr();

  useWSMgrContext();

  const [curBSSet, setBSSet] = useState(currentBoxStyleSet ?? bsMgr?.CurrentBoxStyleSet());
  const [curHierType, setHierType] = useState(() => {
    const existing = bsMgr?.GetHierarchyType();
    if (existing != null) return existing;
    return isPositionChart ? enumHierarchyTypes.positions : enumHierarchyTypes.employees;
  });
  const [selectedBox, setSelectedBox] = useState(null);
  const [bsPanel, setBSPanel] = useState(new QBoxStylesPanel());
  const canvasRef = useRef(null);
  const [bStylesDirty, setBStylesDirty] = useState(false);
  const { width, height } = GetPanelDimensions(Math.max(minWidth, customWidth));
  const scrollContainerRef = useRef(null);
  const pendingRafRef = useRef(null);
  const openCtrlRef = useRef(null);
  const floatingCtrlRef = useRef(null);

  const { canAccessGrouping } = usePermissionCheck();
  const uInfo = wsMgr?.GetUserInfo();
  const allowEdit = uInfo?.CanUpdateTemplates();
  const doControls = showControls && allowEdit;
  const isEditableSet = [enumBoxStyleSet.sharedboxstyles, enumBoxStyleSet.userboxstyles].includes(
    curBSSet
  );

  useTopic("chartDocumentLoaded", docRef => {
    const isPosition = docRef?.IsPositionDocument() ?? false;
    const hierType = isPosition ? enumHierarchyTypes.positions : enumHierarchyTypes.employees;
    handleHierarchyTypeChange(hierType);
  });

  // initialize box styles panel and set state for re-use
  useEffect(() => {
    bsPanel.fDblClick = doControls && isEditableSet ? "EditBoxStyle" : null;
    bsPanel.SizePanel(width, height);
    bsPanel.SetScaleFactor(scaleFactor);
    bsPanel.InitBoxStylesPanel(wsMgr);
    bsPanel.SetFillColor("#FFFFFF");
    bsPanel.PositionPanel(0, 0);
    bsPanel.UpdatePanel();

    const hierType =
      bsMgr.fPendingScrollTarget != null
        ? bsMgr.GetHierarchyType()
        : isPositionChart
          ? enumHierarchyTypes.positions
          : enumHierarchyTypes.employees;
    handleHierarchyTypeChange(hierType);

    if (bsMgr.fPendingScrollTarget != null) {
      setSelectedBox(bsPanel.GetSelectedBoxStyle() ?? null);
      const rafId = requestAnimationFrame(() => {
        scrollToSelectedElement();
        bsMgr.SetPendingScrollTarget(null);
      });
      pendingRafRef.current = rafId;
    }

    return () => {
      if (pendingRafRef.current != null) {
        cancelAnimationFrame(pendingRafRef.current);
        pendingRafRef.current = null;
      }
      wsMgr?.DropPanelReference(bsPanel);
    };
  }, [customWidth]);

  // scale canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = width;
    canvas.height = bsPanel.GetHeight();
    setupCanvas(canvas);
  }, [canvasRef, customWidth]);

  useEffect(() => {
    if (hierarchyType === undefined) {
      return;
    }

    handleHierarchyTypeChange(hierarchyType);
  }, [hierarchyType]);

  // render QPanel elements
  const draw = () => {
    const canvas = canvasRef.current;
    let newHeight = bsPanel.GetHeight();
    if (newHeight < 300) newHeight = 300;
    resizeCanvas(canvas, width, newHeight);
    bsPanel.fBackBuffer = canvas;
    bsPanel.fDblClick = doControls && isEditableSet ? "EditBoxStyle" : null;
    bsPanel.RenderPanel();
  };

  // redraw box styles html canvas
  useEffect(() => {
    draw();
  }, [curBSSet, selectedBox, customWidth]);

  // change box styles type
  const handleTabChange = currentTabIndex => {
    setBSSet(currentTabIndex);
    bsMgr.SwitchBoxStyles(currentTabIndex);
    bsPanel.UpdatePanel();
  };

  const handleHierarchyTypeChange = hierType => {
    const hierTypeValue = Array.isArray(hierType) && hierType.length > 0 ? hierType[0] : hierType;
    setHierType(hierTypeValue);
    bsMgr.SetHierarchyType(hierTypeValue);
    bsPanel.UpdatePanel();
    draw();
  };

  // canvas event listener for selecting box styles
  const handleBoxLibClick = event => {
    var canvas = event.currentTarget;
    var rect = canvas.getBoundingClientRect();
    // return if not left or right mouse click
    if (event.button != 0 && event.button != 2) return;
    var p = EventToCanvas(event, rect.left, rect.top);
    let panelHitInfo = bsPanel.HitTest(p.x, p.y, null);
    let targetBox = panelHitInfo.fElementHit;
    if (targetBox && targetBox.GetLayout) {
      bsPanel.ClearSelection();
      bsPanel.DoSelectElement(targetBox, true);
      setSelectedBox(targetBox);

      if (onBoxSelect) {
        const selectedBox = bsPanel.GetSelectedBoxStyle();
        if (selectedBox) {
          const l = selectedBox.GetLayout();
          onBoxSelect(l);
        }
      }
    } else {
      bsPanel.ClearSelection();
      setSelectedBox(null);
    }
    return { panelHitInfo, p };
  };

  const handleBoxLibDoubleClick = event => {
    const { panelHitInfo } = handleBoxLibClick(event);
    const box = panelHitInfo?.fElementHit;
    const dblClickCmd = box?.GetDblClickCmd();
    if (dblClickCmd) handleToolClick(dblClickCmd);
  };

  const handleRightClick = event => {
    if (!doControls || !isEditableSet) return;

    const { panelHitInfo } = handleBoxLibClick(event);
    const box = panelHitInfo?.fElementHit;
    if (box && box.GetLayout) {
      openCtrlRef?.current?.setOpen(true);
      floatingCtrlRef?.current?.setPositionReference(event);
    }
    event.preventDefault();
  };

  const closePopover = () => {
    openCtrlRef?.current?.setOpen(false);
  };

  const handleBoxClick = event => {
    switch (event.detail) {
      // single click file handling
      case 1:
        handleBoxLibClick(event);
        break;
      case 2:
        if (event.button !== 0) {
          return;
        }

        // double click file handling
        handleBoxLibDoubleClick(event);
        break;
      default:
    }
  };

  const handleToolClick = code => {
    let clearSelection = true;
    const eventTracker = wsMgr.GetUsageTracker();
    switch (code) {
      case "EditBoxStyle": {
        const { editLayout, previewObj } = bsPanel.EditBoxStyle();
        DoBoxLayoutEditor(editLayout, previewObj, true);
        eventTracker.ReportEvent("box_layout_editor.box_styles.opened");
        break;
      }
      case "LoadBoxStyles":
        LoadBStyles();
        clearSelection = false;
        break;
      case "SaveBoxStyles":
        SaveBStyles();
        clearSelection = false;
        break;
      case "CopyBoxStyle":
        setBSSet(enumBoxStyleSet.sharedboxstyles);
        bsPanel.DoBoxStyleCmd("CopyBoxStyle");
        break;
      default:
        bsPanel.DoBoxStyleCmd(code);
    }
    Redraw(false);

    if (code === "AddBoxStyle" || code === "DuplicateBoxStyle" || code === "CopyBoxStyle") {
      scrollToSelectedElement();
    }

    wsMgr.UpdateReactContext();
    StateLib.ReRenderCanvas();
    closePopover();
  };

  const SaveBStyles = async () => {
    try {
      const { success } = await SaveBoxStyles();
      wsMgr.UploadBoxStylesResult(success);
      const eventTracker = wsMgr.GetUsageTracker();
      eventTracker.ReportEvent("box_layout_editor.box_styles.saved");
      Redraw(true);
      StateLib.ReRenderCanvas();
    } catch {
      wsMgr.UploadBoxStylesResult(false);
    }
  };

  const LoadBStyles = async () => {
    const { success, result } = await LoadBoxStyles();
    wsMgr.DownloadBoxStylesResult(success, result, curBSSet);
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("box_layout_editor.box_styles.opened");
    Redraw(true);
    StateLib.ReRenderCanvas();
  };

  const scrollToSelectedElement = () => {
    const container = scrollContainerRef.current;
    const selected = bsPanel.GetSelectedBoxStyle();
    if (!container || !selected) return;

    const elementTop = selected.GetPositionY();
    const elementBottom = elementTop + selected.GetHeight();
    const { scrollTop, clientHeight } = container;

    if (elementTop < scrollTop || elementBottom > scrollTop + clientHeight) {
      container.scrollTo({
        top: Math.max(0, elementTop - 16),
        behavior: "smooth"
      });
    }
  };

  const Redraw = clearSelection => {
    bsPanel.UpdatePanel();
    if (clearSelection) {
      bsPanel.ClearSelection();
      setSelectedBox(null);
    }
    draw();

    //reload current view
    let curView = wsMgr.GetDoc().GetActiveTemplate();
    wsMgr.DoSetView(curView.fTemplateID);
  };

  const validateName = newName => {
    if (!newName) {
      return { success: false };
    }

    return { success: bsMgr.IsUniqueBoxStyleName(newName, selectedBox.GetLayout()) };
  };

  const onRenameCommit = newName => {
    const wsMgr = StateLib.GetWSMgr();
    let l = selectedBox.GetLayout();
    if (l == null) return null;
    l.SetBoxStyleName(newName);
    l.fDirty = true;
    wsMgr.fBoxStylesMgr.SetDirty();
    StateLib.ReRenderCanvas();
    closePopover();
  };

  const boxStylesDirty = wsMgr.BoxStylesDirty();
  if (bStylesDirty != boxStylesDirty) setBStylesDirty(boxStylesDirty);

  const tabsStyle = { borderBottom: "1px solid #ececed" };
  const tabContentClass = doControls
    ? "oc-right-panel-tabs-content"
    : "oc-right-panel-tabs-content-empty";

  let currentLayoutName = "";
  if (selectedBox && selectedBox.GetLayout) {
    currentLayoutName = selectedBox.GetLayout()?.GetBoxStyleName();
  }

  const showHierarchyToggle = isPositionChart && hierarchyType === undefined;
  const showGroupingToggle = !isPositionChart && canAccessGrouping && hierarchyType === undefined;

  return (
    <Flex direction={"column"} height={"100%"} width={"100%"}>
      {showTabs && (
        <div style={doControls ? tabsStyle : null} id={"bs-container"}>
          <Tabs
            tabSize={"medium"}
            tabIndex={curBSSet}
            customClass={"ocn-sidebar-tabs"}
            customHeaderClass={"oc-tab-header-no-divider"}
            customContentClass={tabContentClass}
            tabs={GetTabs(bsPanel, doControls, handleToolClick)}
            onTabChange={handleTabChange}
          />
        </div>
      )}
      {showHierarchyToggle && (
        <Flex justifyContent="center" padding="8px">
          <TogglePillGroup
            data-testid="boxlibrary-container-type-toggle"
            value={curHierType}
            onChange={handleHierarchyTypeChange}
            exclusive
            enforceValueSet
          >
            <TogglePill
              data-testid="boxlibrary-container-type-toggle-position"
              size="small"
              value={enumHierarchyTypes.positions}
            >
              {appstr("Position")}
            </TogglePill>
            <TogglePill
              data-testid="boxlibrary-container-type-toggle-employee"
              size="small"
              value={enumHierarchyTypes.employees}
            >
              {appstr("Employee")}
            </TogglePill>
          </TogglePillGroup>
        </Flex>
      )}
      {showGroupingToggle && (
        <Flex justifyContent="center" padding="8px">
          <TogglePillGroup
            data-testid="boxlibrary-hierarchy-type-toggle"
            value={curHierType}
            onChange={handleHierarchyTypeChange}
            exclusive
            enforceValueSet
          >
            <TogglePill
              data-testid="boxlibrary-hierarchy-type-toggle-employee"
              size="small"
              value={enumHierarchyTypes.employees}
            >
              {appstr("Employee")}
            </TogglePill>
            <TogglePill
              data-testid="boxlibrary-hierarchy-type-toggle-grouping"
              size="small"
              value={enumHierarchyTypes.grouping}
            >
              {appstr("Grouping")}
            </TogglePill>
          </TogglePillGroup>
        </Flex>
      )}
      <div
        ref={scrollContainerRef}
        className={cx("sidebar-content-container", "ocn-list-scrollbar")}
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          flex: "1",
          padding: "16px",
          paddingBottom: "0"
        }}
      >
        <canvas
          id={"boxstylescanvas"}
          onMouseDown={handleBoxClick}
          ref={canvasRef}
          onContextMenu={handleRightClick}
        />
        {doControls && (
          <ContextMenu
            openCtrlRef={openCtrlRef}
            floatingCtrlRef={floatingCtrlRef}
            title={TruncateText(currentLayoutName ?? "", "14px Arial", 150)}
            options={[
              {
                label: appstr("Edit"),
                value: "edit",
                icon: "pen",
                clickHandler: () => handleToolClick("EditBoxStyle")
              },
              {
                icon: "font",
                value: "rename",
                dataTestId: "boxlibrary-ctx-rename",
                content: (
                  <RenamePopover
                    name={selectedBox ? selectedBox?.GetLayout()?.GetBoxStyleName() : ""}
                    onClose={Redraw}
                    onCommit={onRenameCommit}
                    validateFxn={validateName}
                    inputProps={{ id: "boxlibrary-ctx-rename-input" }}
                    triggerProps={{
                      tooltipProps: getHoverTipContent("renamebs")
                    }}
                    popoverProps={{
                      reference: <Text size={"small"}>{appstr("Rename")}</Text>
                    }}
                    iconProps={{ size: "small" }}
                  />
                )
              },
              {
                label: appstr("Make a copy"),
                value: "copy",
                icon: "copy",
                divider: true,
                clickHandler: () => handleToolClick("DuplicateBoxStyle")
              },
              {
                label: appstr("Delete"),
                value: "delete",
                icon: "trash",
                clickHandler: () => handleToolClick("DeleteBoxStyles")
              }
            ]}
          />
        )}
      </div>
    </Flex>
  );
};

export default BoxLibrary;
